<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const logger = useLogger("FormContainer");

const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

type FieldValidator = () => Promise<{ ok: true } | { ok: false; message: string }> | { ok: true } | { ok: false; message: string };

const fields = new Map<string, FieldValidator>();
const errors = ref<Record<string, string>>({});
const submitState = ref<"idle" | "validating" | "invalid" | "submitting">("idle");

function registerField(id: string, validator: FieldValidator) {
  fields.set(id, validator);
}
function unregisterField(id: string) {
  fields.delete(id);
  if (errors.value[id]) {
    const { [id]: _, ...rest } = errors.value;
    errors.value = rest;
  }
}
function setFieldError(id: string, message: string | null) {
  if (!message) {
    if (errors.value[id]) {
      const { [id]: _, ...rest } = errors.value;
      errors.value = rest;
    }
    return;
  }
  errors.value = { ...errors.value, [id]: message };
}
async function validateAll(): Promise<boolean> {
  submitState.value = "validating";
  const next: Record<string, string> = {};
  for (const [fieldId, run] of fields.entries()) {
    try {
      const result = await Promise.resolve(run());
      if (result && (result as any).ok === false) {
        next[fieldId] = (result as any).message || "Invalid value";
      }
    } catch (e) {
      logger.error("Validator threw", e);
    }
  }
  errors.value = next;
  const ok = Object.keys(next).length === 0;
  submitState.value = ok ? "idle" : "invalid";
  return ok;
}

provide("form-context", {
  id: props.id,
  registerField,
  unregisterField,
  setFieldError,
  validateAll,
  errors,
  submitState,
});

eventBus.on(`component:onSubmit:${props.id}`, async () => {
  const ok = await validateAll();
  if (ok) {
    eventBus.emit(`component:onFormValid:${props.id}`, { id: props.id });
  } else {
    eventBus.emit(`component:onFormInvalid:${props.id}`, {
      id: props.id,
      errors: errors.value,
    });
    if (typeof window !== "undefined") {
      const firstId = Object.keys(errors.value)[0];
      if (firstId) {
        const el = document.getElementById(firstId);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }
});


const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Form",
    description: "Configure this form",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Form options",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Form",
    icon: "lucide:clipboard-list",
  },
});

const errorList = computed(() =>
  Object.entries(errors.value).map(([id, message]) => ({ id, message }))
);

const showErrorSummary = computed(
  () => !!definitionData.value?.showErrorSummary && errorList.value.length > 0
);

const classes = computed(() => {
  const settings = definitionData.value?.columnsSettings ?? { default: 1 };
  let classList = "grid";
  if (settings.default) classList += ` grid-cols-${settings.default}`;
  return classList;
});
</script>

<template>
  <form
    :class="cn('form-container', $attrs.class ?? '')"
    novalidate
    @submit.prevent
  >
    <div v-if="showErrorSummary" class="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
      <p class="font-medium text-destructive mb-1">{{ definitionData?.errorSummaryTitle || "Please fix the errors below" }}</p>
      <ul class="list-disc list-inside text-destructive">
        <li v-for="err in errorList" :key="err.id">{{ err.message }}</li>
      </ul>
    </div>
    <div :class="classes">
      <slot />
    </div>
  </form>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.FORM_CONTAINER,
  editor: () => import("#bb/components/editor/container/form.vue"),
  id: "form-container",
  icon: "lucide:clipboard-list",
  type: "Form",
  description: "Groups assessment inputs and validates them on submit.",
  default: {
    columnsSettings: { default: 1 },
    showErrorSummary: false,
    errorSummaryTitle: "Please fix the errors below",
    children: [],
  },
  categories: ["Containers"],
  order: 12005,
};
</script>
